import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Popover, PopoverBody, PopoverHeader } from 'reactstrap';
import classNames from 'classnames';

import { Button, Icon } from 'js/components';
import { faCheck, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import {
  closeSlideOutSidebar,
  SLIDE_OUT_SIDEBAR_QUERY,
} from 'js/utils/sidebar';

import { useOnboarding } from './OnboardingContext';
import { CARD_ID } from './constants';
import { countFinished } from './steps';
import { StepMarker, StepTitle } from './StepParts';
import ProgressRing from './ProgressRing';
import styles from './Onboarding.module.scss';

function StepsPopover({ onboarding, target, isOpen, toggle, onClose }) {
  const { steps, workspaceId, dismiss } = onboarding;

  return (
    <Popover
      target={target}
      isOpen={isOpen}
      toggle={toggle}
      trigger="legacy"
      placement="right-end"
      // The pill sits in the scrolling sidebar, which Popper would otherwise
      // treat as the space available and flip the popover over the menu.
      boundariesElement="viewport"
      flip={false}
      popperClassName={styles.popover}
    >
      <PopoverHeader>Run your first team assessment</PopoverHeader>
      <PopoverBody className="p-0">
        <ol className="list-unstyled mb-0 py-1">
          {steps.map((step) => (
            <li
              key={step.key}
              className={classNames(styles['popover-step'], {
                [styles['popover-step-next']]: step.isNext,
              })}
            >
              <StepMarker step={step} small />
              <span className="d-flex flex-column align-items-start">
                <StepTitle step={step} />
                {step.isNext && step.cta && (
                  <Button
                    tag={Link}
                    to={step.cta.to}
                    color="primary"
                    size="sm"
                    className="m-0 mt-1"
                    onClick={onClose}
                  >
                    {step.cta.label}
                  </Button>
                )}
              </span>
            </li>
          ))}
        </ol>
        <div className="d-flex justify-content-between align-items-center border-top px-3 py-2">
          <Link to={`/w/${workspaceId}/dashboard`} onClick={onClose}>
            Open on dashboard
          </Link>
          <Button
            color="link"
            size="sm"
            className="m-0 p-0"
            onClick={() => {
              onClose();
              dismiss();
            }}
          >
            Hide checklist
          </Button>
        </div>
      </PopoverBody>
    </Popover>
  );
}

/**
 * The checklist's entry pinned below the sidebar menu. On the dashboard it
 * scrolls to the card. Elsewhere it opens a popover beside the sidebar, except
 * when the sidebar is the slide-out menu (phones), where it goes to the
 * dashboard card instead.
 */
export default function GettingStartedPill() {
  const onboarding = useOnboarding();
  const { pathname } = useLocation();
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const pillRef = useRef(null);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  if (!onboarding?.progress) {
    return null;
  }

  const { progress, steps, workspaceId, refresh } = onboarding;
  const finished = countFinished(progress);
  const next = steps.find((step) => step.isNext);
  const onDashboard = pathname === `/w/${workspaceId}/dashboard`;
  const slideOut = () => window.matchMedia(SLIDE_OUT_SIDEBAR_QUERY).matches;

  // The popover's own open/close (pill click, click outside, Escape), used
  // only where the pill opens it.
  const togglePopover = () => {
    if (onDashboard || slideOut()) {
      return;
    }

    // Steps can be finished without leaving the page (favoriting a team), so
    // check again whenever the popover opens.
    if (!open) {
      refresh();
    }

    setOpen(!open);
  };

  const handleClick = () => {
    const card = onDashboard && document.getElementById(CARD_ID);

    if (card) {
      closeSlideOutSidebar();
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (slideOut()) {
      // The card leads the dashboard, and navigating closes the menu.
      history.push(`/w/${workspaceId}/dashboard`);
    }
  };

  return (
    <div className={classNames('sidebar-footer', styles['pill-container'])}>
      <button
        ref={pillRef}
        type="button"
        className={styles.pill}
        aria-expanded={onDashboard ? undefined : open}
        onClick={handleClick}
      >
        {progress.complete ? (
          <span
            className={classNames(styles.marker, styles['marker-pill-done'])}
          >
            <Icon icon={faCheck} />
          </span>
        ) : (
          <ProgressRing done={finished} total={steps.length} inverse />
        )}
        <span className={classNames(styles.fill, styles['pill-text'])}>
          <span className={styles['pill-title']}>
            {progress.complete ? 'All set' : 'Getting started'}
          </span>
          {!progress.complete && (
            <span
              className={classNames(styles['pill-subtitle'], 'text-truncate')}
            >
              {next
                ? `Next: ${next.title}`
                : `${finished} of ${steps.length} done`}
            </span>
          )}
        </span>
        <span className="sr-only">
          {finished} of {steps.length} steps done
        </span>
        <Icon icon={faChevronRight} className={styles['pill-chevron']} />
      </button>
      <StepsPopover
        onboarding={onboarding}
        target={pillRef}
        isOpen={open}
        toggle={togglePopover}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}
